import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  NoteAdd as NoteAddIcon,
  EditNote as EditNoteIcon,
} from "@mui/icons-material";
import { api } from "../../../../api/api";
import { RightPanel } from "../../../../components/RightPanel";
import { useTimezoneFormatter } from "../../../../hooks/useTimezoneFormatter";

interface IHistoryEntry {
  history_uuid: string;
  module_name: string;
  message: string;
  payload: {
    identifier: { column_name: string; column_value: string };
    changed_column?: Record<string, { old_value: any; new_value: any }>;
    no_changed_column?: Record<string, any>;
  };
  remark: string;
  status: "CREATE" | "UPDATE";
  modified_by_name: string | null;
  create_ts: string;
}

interface IRoughEstimateHistoryProps {
  roughEstimateUuid: string;
  buttonSx?: Record<string, any>;
}

const SKIP_FIELDS = [
  "updated_by_uuid",
  "company_outlet_name",
  "company_outlet_uuid",
  "created_by_uuid",
  "modified_by_uuid",
  "function_date",
  "sub_total",
  "total_gst",
  "grand_total",
  "tax_amount",
  "total_discount",
  "sub_total_less_discount",
];

const FIELD_LABELS: Record<string, string> = {
  sub_total: "Sub Total",
  grand_total: "Grand Total",
  items_charges: "Items / Charges",
  stay_details: "Stay Details",
  function_date: "Function Date",
  payment_status: "Payment Status",
  remaining_balance: "Remaining Balance",
  attachments: "Attachments",
  discount: "Discount",
  discount_mode: "Discount Mode",
  gst_percentage: "GST/VAT",
  gst_amount: "GST Amount",
  vat_percentage: "VAT %",
  vat_amount: "VAT Amount",
  tax_type: "Tax Type",
  guest_count: "Guest Count",
  remarks: "Remarks",
  terms_conditions: "Terms & Conditions",
};

const getFieldLabel = (field: string): string =>
  FIELD_LABELS[field] || field.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const formatValue = (value: any, fieldName: string): string => {
  if (value === null || value === undefined) return "--";
  if (fieldName.includes("date") && typeof value === "string" && value.includes("T")) {
    try {
      return new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return String(value); }
  }
  if (fieldName.includes("total") || fieldName.includes("balance") || fieldName === "sub_total") {
    const num = Number(value);
    if (!isNaN(num)) return `₹${num.toLocaleString("en-IN")}`;
  }
  if (Array.isArray(value)) return `${value.length} item(s)`;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

// ─── Main Component ────────────────────────────────────────────────────────────

export const RoughEstimateHistory: React.FC<IRoughEstimateHistoryProps> = ({ roughEstimateUuid, buttonSx }) => {
  const [open, setOpen] = React.useState(false);
  const [history, setHistory] = React.useState<IHistoryEntry[]>([]);
  const [loading, setLoading] = React.useState(false);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/history/get-history?module_uuid=${roughEstimateUuid}`);
      setHistory(res.data.data?.result || []);
    } catch (err) {
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    fetchHistory();
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={<HistoryIcon fontSize="small" />}
        onClick={handleOpen}
        sx={buttonSx}
      >
        History
      </Button>

      <RightPanel
        open={open}
        heading="Quotation History"
        width="40%"
        onClose={() => setOpen(false)}
      >
        <Box sx={{ px: 2.5, py: 2 }}>
          {loading ? (
            <Stack spacing={3}>
              {[1, 2, 3].map((i) => (
                <Box key={i} sx={{ display: "flex", gap: 2 }}>
                  <Skeleton variant="circular" width={36} height={36} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="rounded" height={80} sx={{ mt: 1 }} />
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : history.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <HistoryIcon sx={{ fontSize: 48, color: "grey.300", mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No history found for this quotation.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={0}>
              {history.map((entry, idx) => (
                <HistoryTimelineEntry
                  key={entry.history_uuid}
                  entry={entry}
                  isLast={idx === history.length - 1}
                />
              ))}
            </Stack>
          )}
        </Box>
      </RightPanel>
    </>
  );
};

// ─── Timeline Entry ────────────────────────────────────────────────────────────

const HistoryTimelineEntry: React.FC<{ entry: IHistoryEntry; isLast: boolean }> = ({ entry, isLast }) => {
  const theme = useTheme();
  const { ftDateTime } = useTimezoneFormatter();
  const isCreate = entry.status === "CREATE";
  const userName = entry.modified_by_name || "System";
  const initials = userName.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase();

  const changedFieldsRaw = entry.payload?.changed_column
    ? Object.entries(entry.payload.changed_column).filter(([key]) => !SKIP_FIELDS.includes(key))
    : [];

  // Filter out scalar fields where old and new are effectively the same value
  const isScalarSame = (oldVal: any, newVal: any): boolean => {
    if (oldVal === newVal) return true;
    // If old_value is null/undefined, it's not a real change — API just echoing current state
    if (oldVal === null || oldVal === undefined) return true;
    if (newVal === null || newVal === undefined) return false;
    // Numeric comparison (handles "20170000" vs 20170000 vs "20170000.00")
    const oldNum = Number(oldVal);
    const newNum = Number(newVal);
    if (!isNaN(oldNum) && !isNaN(newNum) && oldNum === newNum) return true;
    // String comparison after trim
    if (String(oldVal).trim() === String(newVal).trim()) return true;
    return false;
  };

  // Check if array field has real changes (not just empty rows)
  const hasRealArrayChanges = (oldVal: any, newVal: any): boolean => {
    const oldArr: any[] = Array.isArray(oldVal) ? oldVal : [];
    const newArr: any[] = Array.isArray(newVal) ? newVal : [];
    const isEmptyRow = (item: any) => {
      if (!item) return true;
      const name = item.name || item.room_name || "";
      const count = Number(item.count || 0);
      const amount = Number(item.amount || 0);
      const total = Number(item.total || 0);
      const price = Number(item.price || 0);
      return !name && count === 0 && amount === 0 && total === 0 && price === 0;
    };
    const filteredOld = oldArr.filter((i) => !isEmptyRow(i));
    const filteredNew = newArr.filter((i) => !isEmptyRow(i));
    const getKey = (item: any) => item?.name || item?.room_name || JSON.stringify(item);
    const oldKeys = filteredOld.map(getKey);
    const newKeys = filteredNew.map(getKey);
    const added = filteredNew.filter((item) => !oldKeys.includes(getKey(item)));
    const removed = filteredOld.filter((item) => !newKeys.includes(getKey(item)));
    const modified = filteredNew.filter((item) => {
      const key = getKey(item);
      const oldItem = filteredOld.find((o) => getKey(o) === key);
      if (!oldItem) return false;
      return JSON.stringify(oldItem) !== JSON.stringify(item);
    });
    return added.length > 0 || removed.length > 0 || modified.length > 0;
  };

  const changedFields = changedFieldsRaw.filter(([key, change]) => {
    if (key === "items_charges" || key === "stay_details") {
      return hasRealArrayChanges(change.old_value, change.new_value);
    }
    return !isScalarSame(change.old_value, change.new_value);
  });

  // Count actual changes: for array fields, count each added/modified/removed item individually
  const getItemChangeCount = (change: { old_value: any; new_value: any }): number => {
    const oldItems: any[] = Array.isArray(change.old_value) ? change.old_value : [];
    const newItems: any[] = Array.isArray(change.new_value) ? change.new_value : [];
    const isEmptyRow = (item: any) => {
      if (!item) return true;
      const name = item.name || item.room_name || "";
      const count = Number(item.count || 0);
      const amount = Number(item.amount || 0);
      const total = Number(item.total || 0);
      const price = Number(item.price || 0);
      return !name && count === 0 && amount === 0 && total === 0 && price === 0;
    };
    const getKey = (item: any) => item?.name || item?.room_name || JSON.stringify(item);
    const filteredOld = oldItems.filter((i) => !isEmptyRow(i));
    const filteredNew = newItems.filter((i) => !isEmptyRow(i));
    const oldKeys = filteredOld.map(getKey);
    const newKeys = filteredNew.map(getKey);
    const added = filteredNew.filter((item) => !oldKeys.includes(getKey(item))).length;
    const removed = filteredOld.filter((item) => !newKeys.includes(getKey(item))).length;
    const modified = filteredNew.filter((item) => {
      const key = getKey(item);
      const oldItem = filteredOld.find((o) => getKey(o) === key);
      if (!oldItem) return false;
      return JSON.stringify(oldItem) !== JSON.stringify(item);
    }).length;
    return added + removed + modified;
  };

  const totalChangeCount = changedFields.reduce((count, [key, change]) => {
    if (key === "items_charges" || key === "stay_details") {
      return count + getItemChangeCount(change);
    }
    return count + 1;
  }, 0);

  const itemChanges = changedFields.filter(([key]) => key === "items_charges" || key === "stay_details");
  const otherChanges = changedFields.filter(([key]) => key !== "items_charges" && key !== "stay_details");

  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      {/* Left: Avatar + vertical line */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Avatar
          sx={{
            width: 36,
            height: 36,
            fontSize: "0.75rem",
            fontWeight: 700,
            bgcolor: isCreate ? "success.main" : "primary.main",
            color: "#fff",
            boxShadow: `0 2px 8px ${isCreate ? "rgba(46,125,50,0.3)" : "rgba(25,118,210,0.3)"}`,
          }}
        >
          {isCreate ? <NoteAddIcon sx={{ fontSize: 18 }} /> : initials}
        </Avatar>
        {!isLast && (
          <Box
            sx={{
              width: 2,
              flex: 1,
              mt: 1,
              background: `linear-gradient(to bottom, ${theme.palette.grey[300]}, ${theme.palette.grey[200]})`,
              borderRadius: 1,
            }}
          />
        )}
      </Box>

      {/* Right: Heading outside card + accordion card below */}
      <Box sx={{ flex: 1, pb: 3 }}>
        {/* Heading — outside the card */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" fontWeight={700} color="text.primary" sx={{ fontSize: "0.85rem" }}>
              {userName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
              {isCreate ? "created the quotation" : "made changes"}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.7rem",
              bgcolor: theme.palette.grey[100],
              px: 1,
              py: 0.25,
              borderRadius: 1,
              whiteSpace: "nowrap",
            }}
          >
            {ftDateTime(entry.create_ts)}
          </Typography>
        </Box>

        {/* CREATE — show initial items/stay details from no_changed_column */}
        {isCreate && (
          <Box>
            {(() => {
              const initialData = entry.payload?.no_changed_column;
              const items: any[] = initialData && Array.isArray(initialData.items_charges)
                ? initialData.items_charges.filter((i: any) => i && (i.name || i.room_name))
                : [];
              const stays: any[] = initialData && Array.isArray(initialData.stay_details)
                ? initialData.stay_details.filter((i: any) => i && (i.name || i.room_name))
                : [];
              const totalItems = items.length + stays.length;

              return (
                <Accordion
                  disableGutters
                  elevation={0}
                  defaultExpanded={false}
                  sx={{
                    borderRadius: "12px !important",
                    border: `1px solid ${theme.palette.grey[200]}`,
                    overflow: "hidden",
                    "&:before": { display: "none" },
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    transition: "box-shadow 0.2s",
                    "&:hover": { boxShadow: "0 2px 12px rgba(0,0,0,0.08)" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "success.main", fontSize: 20 }} />}
                    sx={{
                      minHeight: 40,
                      px: 2,
                      bgcolor: "rgba(46, 125, 50, 0.04)",
                      borderBottom: `1px solid ${theme.palette.grey[100]}`,
                      "& .MuiAccordionSummary-content": { my: 0.75, alignItems: "center", gap: 1 },
                    }}
                  >
                    <NoteAddIcon sx={{ fontSize: 16, color: "success.main" }} />
                    <Typography variant="caption" fontWeight={600} color="success.dark" sx={{ fontSize: "0.78rem" }}>
                      New Quotation Created
                    </Typography>
                    {totalItems > 0 && (
                      <Chip
                        label={`${items.length > 0 ? `${items.length} item${items.length > 1 ? "s" : ""}` : ""}${items.length > 0 && stays.length > 0 ? " + " : ""}${stays.length > 0 ? `${stays.length} stay${stays.length > 1 ? "s" : ""}` : ""}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: "0.6rem", borderColor: "success.light", color: "success.dark" }}
                      />
                    )}
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2 }}>
                    {items.length === 0 && stays.length === 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        No items data available
                      </Typography>
                    ) : (
                      <Box sx={{ borderRadius: 1.5, overflow: "hidden", border: `1px solid ${theme.palette.grey[200]}` }}>
                        {items.length > 0 && (
                          <>
                            <Box sx={{ bgcolor: theme.palette.grey[50], px: 1.5, py: 0.5 }}>
                              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.6rem", letterSpacing: 0.5 }}>
                                Items / Charges
                              </Typography>
                            </Box>
                            <Box sx={{ display: "grid", gridTemplateColumns: "1.2fr 0.5fr 0.5fr 0.4fr 0.4fr 0.5fr 0.7fr", bgcolor: theme.palette.grey[100], px: 1.5, py: 0.5, gap: 0.5 }}>
                              <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary">ITEM</Typography>
                              <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">QTY</Typography>
                              <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">RATE</Typography>
                              <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">DISC.</Typography>
                              <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">GST%</Typography>
                              <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">GST AMT</Typography>
                              <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">TOTAL</Typography>
                            </Box>
                            {items.map((item: any, i: number) => (
                              <Box key={i} sx={{ display: "grid", gridTemplateColumns: "1.2fr 0.5fr 0.5fr 0.4fr 0.4fr 0.5fr 0.7fr", px: 1.5, py: 0.5, gap: 0.5, borderBottom: i < items.length - 1 ? `1px solid ${theme.palette.grey[100]}` : "none", alignItems: "center" }}>
                                <Typography variant="caption" fontSize="0.7rem" fontWeight={600} color="success.dark">{item.name || "Item"}</Typography>
                                <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{item.count || 0}</Typography>
                                <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{`\u20B9${item.amount || 0}`}</Typography>
                                <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{item.discount || 0}</Typography>
                                <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{item.gst_percentage || 0}%</Typography>
                                <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{`\u20B9${Number(item.gst_amount || 0).toLocaleString("en-IN")}`}</Typography>
                                <Typography variant="caption" fontSize="0.7rem" fontWeight={700} color="success.dark" textAlign="right">{`\u20B9${Number(item.total || 0).toLocaleString("en-IN")}`}</Typography>
                              </Box>
                            ))}
                          </>
                        )}
                        {stays.length > 0 && (
                          <>
                            <Box sx={{ bgcolor: theme.palette.grey[50], px: 1.5, py: 0.5, borderTop: items.length > 0 ? `1px solid ${theme.palette.grey[200]}` : "none" }}>
                              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.6rem", letterSpacing: 0.5 }}>
                                Stay Details
                              </Typography>
                            </Box>
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 0.6fr 0.4fr 0.4fr 0.45fr 0.45fr 0.35fr 0.35fr 0.45fr 0.6fr", bgcolor: theme.palette.grey[100], px: 1.5, py: 0.5, gap: 0.5 }}>
                              <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary">ROOM</Typography>
                              <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="center">CHECK IN/OUT</Typography>
                              <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">ROOMS</Typography>
                              <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">NIGHTS</Typography>
                              <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">PRICE</Typography>
                              <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">SUB TOT</Typography>
                              <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">DISC.</Typography>
                              <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">GST%</Typography>
                              <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">GST AMT</Typography>
                              <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">TOTAL</Typography>
                            </Box>
                            {stays.map((item: any, i: number) => (
                              <Box key={i} sx={{ display: "grid", gridTemplateColumns: "1fr 0.6fr 0.4fr 0.4fr 0.45fr 0.45fr 0.35fr 0.35fr 0.45fr 0.6fr", px: 1.5, py: 0.5, gap: 0.5, borderBottom: i < stays.length - 1 ? `1px solid ${theme.palette.grey[100]}` : "none", alignItems: "center" }}>
                                <Typography variant="caption" fontSize="0.7rem" fontWeight={600} color="success.dark">{item.room_name || item.name || "Room"}</Typography>
                                <Box sx={{ textAlign: "center" }}><Typography variant="caption" fontSize="0.65rem" color="success.dark" noWrap>{item.check_in ? item.check_in.substring(5) : "--"}|{item.check_out ? item.check_out.substring(5) : "--"}</Typography></Box>
                                <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{item.rooms || 0}</Typography>
                                <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{item.nights || 0}</Typography>
                                <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{`\u20B9${item.price || 0}`}</Typography>
                                <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{`\u20B9${Number(item.sub_total || 0).toLocaleString("en-IN")}`}</Typography>
                                <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{item.discount || 0}</Typography>
                                <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{item.gst_percentage || 0}%</Typography>
                                <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{`\u20B9${Number(item.gst_amount || 0).toLocaleString("en-IN")}`}</Typography>
                                <Typography variant="caption" fontSize="0.7rem" fontWeight={700} color="success.dark" textAlign="right">{`\u20B9${Number(item.total || 0).toLocaleString("en-IN")}`}</Typography>
                              </Box>
                            ))}
                          </>
                        )}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })()}
          </Box>
        )}

        {/* UPDATE — no changes detected */}
        {!isCreate && changedFields.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontStyle: "italic" }}>
            No changes detected
          </Typography>
        )}

        {/* UPDATE — accordion card with changes */}
        {!isCreate && changedFields.length > 0 && (
          <Accordion
            disableGutters
            elevation={0}
            defaultExpanded={false}
            sx={{
              borderRadius: "12px !important",
              border: `1px solid ${theme.palette.grey[200]}`,
              overflow: "hidden",
              "&:before": { display: "none" },
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
              transition: "box-shadow 0.2s",
              "&:hover": {
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "primary.main", fontSize: 20 }} />}
              sx={{
                minHeight: 40,
                px: 2,
                bgcolor: theme.palette.grey[50],
                borderBottom: `1px solid ${theme.palette.grey[100]}`,
                "& .MuiAccordionSummary-content": { my: 0.75, alignItems: "center", gap: 1 },
              }}
            >
              <EditNoteIcon sx={{ fontSize: 16, color: "primary.main" }} />
              <Typography variant="caption" fontWeight={600} color="text.primary" sx={{ fontSize: "0.78rem" }}>
                {totalChangeCount} change{totalChangeCount > 1 ? "s" : ""}
              </Typography>
              <Chip
                label={`${itemChanges.length > 0 ? "Items" : ""}${itemChanges.length > 0 && otherChanges.length > 0 ? " + " : ""}${otherChanges.length > 0 ? `${otherChanges.length} fields` : ""}`}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: "0.6rem", borderColor: theme.palette.grey[300] }}
              />
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              <Stack spacing={2}>
                {/* Items table first */}
                {itemChanges.map(([fieldName, change]) => (
                  <ItemsChangeTable key={fieldName} fieldName={fieldName} change={change} />
                ))}

                {/* Other scalar changes */}
                {otherChanges.length > 0 && (
                  <Box>
                    {itemChanges.length > 0 && (
                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.75, display: "block", textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.6rem" }}>
                        Other Changes
                      </Typography>
                    )}
                    <Box sx={{ borderRadius: 1.5, overflow: "hidden", border: `1px solid ${theme.palette.grey[200]}` }}>
                      {otherChanges.map(([fieldName, change], i) => (
                        <Box
                          key={fieldName}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 1.5,
                            py: 0.75,
                            borderBottom: i < otherChanges.length - 1 ? `1px solid ${theme.palette.grey[100]}` : "none",
                            bgcolor: i % 2 === 0 ? "rgba(0,0,0,0.015)" : "#fff",
                          }}
                        >
                          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: "0.72rem" }}>
                            {getFieldLabel(fieldName)}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            {change.old_value !== undefined && change.old_value !== null && (
                              <>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textDecoration: "line-through",
                                    color: "error.main",
                                    fontSize: "0.72rem",
                                    opacity: 0.85,
                                  }}
                                >
                                  {formatValue(change.old_value, fieldName)}
                                </Typography>
                                <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.8rem" }}>→</Typography>
                              </>
                            )}
                            <Typography variant="caption" sx={{ color: "success.dark", fontWeight: 700, fontSize: "0.72rem" }}>
                              {formatValue(change.new_value, fieldName)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </Box>
  );
};

// ─── Items Change Table ────────────────────────────────────────────────────────

const ItemsChangeTable: React.FC<{
  fieldName: string;
  change: { old_value: any; new_value: any };
}> = ({ fieldName, change }) => {
  const theme = useTheme();
  const oldItems: any[] = Array.isArray(change.old_value) ? change.old_value : [];
  const newItems: any[] = Array.isArray(change.new_value) ? change.new_value : [];

  const getItemKey = (item: any) => item?.name || item?.room_name || JSON.stringify(item);
  const oldKeys = oldItems.map(getItemKey);
  const newKeys = newItems.map(getItemKey);

  // Filter out empty/placeholder rows (no meaningful data)
  const isEmptyRow = (item: any) => {
    if (!item) return true;
    const name = item.name || item.room_name || "";
    const count = Number(item.count || 0);
    const amount = Number(item.amount || 0);
    const total = Number(item.total || 0);
    const price = Number(item.price || 0);
    // Empty if no name and no values
    return !name && count === 0 && amount === 0 && total === 0 && price === 0;
  };

  const filteredOldItems = oldItems.filter((item) => !isEmptyRow(item));
  const filteredNewItems = newItems.filter((item) => !isEmptyRow(item));

  const filteredOldKeys = filteredOldItems.map(getItemKey);
  const filteredNewKeys = filteredNewItems.map(getItemKey);

  const added = filteredNewItems.filter((item) => !filteredOldKeys.includes(getItemKey(item)));
  const removed = filteredOldItems.filter((item) => !filteredNewKeys.includes(getItemKey(item)));
  const modified = filteredNewItems
    .filter((item) => {
      const key = getItemKey(item);
      const oldItem = filteredOldItems.find((o) => getItemKey(o) === key);
      if (!oldItem) return false;
      return JSON.stringify(oldItem) !== JSON.stringify(item);
    })
    .map((item) => ({
      newItem: item,
      oldItem: filteredOldItems.find((o) => getItemKey(o) === getItemKey(item))!,
    }));

  // If no actual changes after filtering, don't render the section at all
  if (added.length === 0 && removed.length === 0 && modified.length === 0) {
    return null;
  }

  const isStay = fieldName === "stay_details";
  const gridCols = isStay
    ? "46px 1fr 80px 35px 35px 45px 45px 35px 35px 50px 65px"
    : "46px 1fr 50px 55px 35px 35px 60px 70px";

  return (
    <Box>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.75, display: "block", textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.6rem" }}>
        {getFieldLabel(fieldName)}
      </Typography>

      <Box sx={{ borderRadius: 1.5, overflow: "hidden", border: `1px solid ${theme.palette.grey[200]}` }}>
        {/* Header */}
        <Box sx={{ display: "grid", gridTemplateColumns: gridCols, bgcolor: theme.palette.grey[100], px: 1, py: 0.6, gap: 0.5 }}>
          <Box />
          <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary">{isStay ? "ROOM" : "ITEM"}</Typography>
          {isStay && <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="center">IN/OUT</Typography>}
          <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">{isStay ? "ROOMS" : "QTY"}</Typography>
          {isStay && <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">NIGHTS</Typography>}
          <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">{isStay ? "PRICE" : "RATE"}</Typography>
          {isStay && <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">SUB TOT</Typography>}
          <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">DISC.</Typography>
          <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">GST%</Typography>
          <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">GST AMT</Typography>
          <Typography variant="caption" fontWeight={700} fontSize="0.6rem" color="text.secondary" textAlign="right">TOTAL</Typography>
        </Box>

        {/* Added */}
        {added.map((item, i) => (
          <Box key={`add-${i}`} sx={{ display: "grid", gridTemplateColumns: gridCols, px: 1, py: 0.6, gap: 0.5, bgcolor: "rgba(46, 125, 50, 0.05)", borderBottom: `1px solid ${theme.palette.grey[100]}`, alignItems: "center" }}>
            <Chip label="New" size="small" color="success" sx={{ height: 18, fontSize: "0.55rem", fontWeight: 700, "& .MuiChip-label": { px: 0.75 } }} />
            <Typography variant="caption" fontSize="0.7rem" color="success.dark" fontWeight={600} noWrap>{item.name || item.room_name || "Item"}</Typography>
            {isStay && <Box sx={{ textAlign: "center", lineHeight: 1.2 }}><Typography variant="caption" fontSize="0.6rem" color="success.dark" display="block">{item.check_in || "--"}</Typography><Typography variant="caption" fontSize="0.6rem" color="success.dark" display="block">{item.check_out || "--"}</Typography></Box>}
            <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{isStay ? (item.rooms || 0) : (item.count || "--")}</Typography>
            {isStay && <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{item.nights || 0}</Typography>}
            <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{`\u20B9${isStay ? (item.price || 0) : (item.amount || 0)}`}</Typography>
            {isStay && <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{`\u20B9${Number(item.sub_total || 0).toLocaleString("en-IN")}`}</Typography>}
            <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{item.discount || 0}</Typography>
            <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{item.gst_percentage || 0}%</Typography>
            <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right">{`\u20B9${Number(item.gst_amount || 0).toLocaleString("en-IN")}`}</Typography>
            <Typography variant="caption" fontSize="0.7rem" color="success.dark" textAlign="right" fontWeight={700}>{`\u20B9${Number(item.total || 0).toLocaleString("en-IN")}`}</Typography>
          </Box>
        ))}

        {/* Modified */}
        {modified.map(({ oldItem, newItem }, i) => (
          <React.Fragment key={`mod-${i}`}>
            <Box sx={{ display: "grid", gridTemplateColumns: gridCols, px: 1, py: 0.4, gap: 0.5, bgcolor: "rgba(237, 108, 2, 0.04)", alignItems: "center", borderTop: `1px solid ${theme.palette.grey[100]}` }}>
              <Chip label="Edited" size="small" color="warning" sx={{ height: 18, fontSize: "0.55rem", fontWeight: 700, "& .MuiChip-label": { px: 0.5 } }} />
              <Typography variant="caption" fontSize="0.7rem" color="error.main" sx={{ textDecoration: "line-through", opacity: 0.8 }} noWrap>{oldItem.name || oldItem.room_name || "Item"}</Typography>
              {isStay && <Box sx={{ textAlign: "center", lineHeight: 1.2 }}><Typography variant="caption" fontSize="0.6rem" color="error.main" display="block" sx={{ textDecoration: "line-through", opacity: 0.8 }}>{oldItem.check_in || "--"}</Typography><Typography variant="caption" fontSize="0.6rem" color="error.main" display="block" sx={{ textDecoration: "line-through", opacity: 0.8 }}>{oldItem.check_out || "--"}</Typography></Box>}
              <DiffCell oldVal={isStay ? oldItem.rooms : oldItem.count} newVal={isStay ? newItem.rooms : newItem.count} isOld />
              {isStay && <DiffCell oldVal={oldItem.nights} newVal={newItem.nights} isOld />}
              <DiffCell oldVal={isStay ? oldItem.price : oldItem.amount} newVal={isStay ? newItem.price : newItem.amount} isOld prefix={"\u20B9"} />
              {isStay && <DiffCell oldVal={oldItem.sub_total} newVal={newItem.sub_total} isOld prefix={"\u20B9"} format />}
              <DiffCell oldVal={oldItem.discount} newVal={newItem.discount} isOld />
              <DiffCell oldVal={oldItem.gst_percentage} newVal={newItem.gst_percentage} isOld suffix="%" />
              <DiffCell oldVal={oldItem.gst_amount} newVal={newItem.gst_amount} isOld prefix={"\u20B9"} format />
              <DiffCell oldVal={oldItem.total} newVal={newItem.total} isOld prefix={"\u20B9"} format />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: gridCols, px: 1, py: 0.4, gap: 0.5, bgcolor: "rgba(46, 125, 50, 0.03)", borderBottom: `1px solid ${theme.palette.grey[100]}`, alignItems: "center" }}>
              <Box />
              <Typography variant="caption" fontSize="0.7rem" color="success.dark" fontWeight={600} noWrap>{newItem.name || newItem.room_name || "Item"}</Typography>
              {isStay && <Box sx={{ textAlign: "center", lineHeight: 1.2 }}><Typography variant="caption" fontSize="0.6rem" color="success.dark" display="block" fontWeight={600}>{newItem.check_in || "--"}</Typography><Typography variant="caption" fontSize="0.6rem" color="success.dark" display="block" fontWeight={600}>{newItem.check_out || "--"}</Typography></Box>}
              <DiffCell oldVal={isStay ? oldItem.rooms : oldItem.count} newVal={isStay ? newItem.rooms : newItem.count} isOld={false} />
              {isStay && <DiffCell oldVal={oldItem.nights} newVal={newItem.nights} isOld={false} />}
              <DiffCell oldVal={isStay ? oldItem.price : oldItem.amount} newVal={isStay ? newItem.price : newItem.amount} isOld={false} prefix={"\u20B9"} />
              {isStay && <DiffCell oldVal={oldItem.sub_total} newVal={newItem.sub_total} isOld={false} prefix={"\u20B9"} format />}
              <DiffCell oldVal={oldItem.discount} newVal={newItem.discount} isOld={false} />
              <DiffCell oldVal={oldItem.gst_percentage} newVal={newItem.gst_percentage} isOld={false} suffix="%" />
              <DiffCell oldVal={oldItem.gst_amount} newVal={newItem.gst_amount} isOld={false} prefix={"\u20B9"} format />
              <DiffCell oldVal={oldItem.total} newVal={newItem.total} isOld={false} prefix={"\u20B9"} format />
            </Box>
          </React.Fragment>
        ))}

        {/* Removed */}
        {removed.map((item, i) => (
          <Box key={`rem-${i}`} sx={{ display: "grid", gridTemplateColumns: gridCols, px: 1, py: 0.6, gap: 0.5, bgcolor: "rgba(211, 47, 47, 0.05)", borderBottom: `1px solid ${theme.palette.grey[100]}`, alignItems: "center" }}>
            <Chip label="Deleted" size="small" color="error" sx={{ height: 18, fontSize: "0.5rem", fontWeight: 700, "& .MuiChip-label": { px: 0.5 } }} />
            <Typography variant="caption" fontSize="0.7rem" color="error.main" sx={{ textDecoration: "line-through" }} noWrap>{item.name || item.room_name || "Item"}</Typography>
            {isStay && <Box sx={{ textAlign: "center", lineHeight: 1.2 }}><Typography variant="caption" fontSize="0.6rem" color="error.main" display="block" sx={{ textDecoration: "line-through" }}>{item.check_in || "--"}</Typography><Typography variant="caption" fontSize="0.6rem" color="error.main" display="block" sx={{ textDecoration: "line-through" }}>{item.check_out || "--"}</Typography></Box>}
            <Typography variant="caption" fontSize="0.7rem" color="error.main" textAlign="right" sx={{ textDecoration: "line-through" }}>{isStay ? (item.rooms || 0) : (item.count || "--")}</Typography>
            {isStay && <Typography variant="caption" fontSize="0.7rem" color="error.main" textAlign="right" sx={{ textDecoration: "line-through" }}>{item.nights || 0}</Typography>}
            <Typography variant="caption" fontSize="0.7rem" color="error.main" textAlign="right" sx={{ textDecoration: "line-through" }}>{`\u20B9${isStay ? (item.price || 0) : (item.amount || 0)}`}</Typography>
            {isStay && <Typography variant="caption" fontSize="0.7rem" color="error.main" textAlign="right" sx={{ textDecoration: "line-through" }}>{`\u20B9${Number(item.sub_total || 0).toLocaleString("en-IN")}`}</Typography>}
            <Typography variant="caption" fontSize="0.7rem" color="error.main" textAlign="right" sx={{ textDecoration: "line-through" }}>{item.discount || 0}</Typography>
            <Typography variant="caption" fontSize="0.7rem" color="error.main" textAlign="right" sx={{ textDecoration: "line-through" }}>{item.gst_percentage || 0}%</Typography>
            <Typography variant="caption" fontSize="0.7rem" color="error.main" textAlign="right" sx={{ textDecoration: "line-through" }}>{`\u20B9${Number(item.gst_amount || 0).toLocaleString("en-IN")}`}</Typography>
            <Typography variant="caption" fontSize="0.7rem" color="error.main" textAlign="right" sx={{ textDecoration: "line-through" }}>{`\u20B9${Number(item.total || 0).toLocaleString("en-IN")}`}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ─── Diff Cell ─────────────────────────────────────────────────────────────────

const DiffCell: React.FC<{
  oldVal: any;
  newVal: any;
  isOld: boolean;
  prefix?: string;
  suffix?: string;
  format?: boolean;
}> = ({ oldVal, newVal, isOld, prefix = "", suffix = "", format = false }) => {
  const changed = String(oldVal) !== String(newVal);
  const val = isOld ? oldVal : newVal;
  const displayVal = format
    ? `${prefix}${Number(val || 0).toLocaleString("en-IN")}${suffix}`
    : `${prefix}${val || 0}${suffix}`;

  if (!changed) {
    return (
      <Typography variant="caption" fontSize="0.7rem" color="text.secondary" textAlign="right">
        {displayVal}
      </Typography>
    );
  }

  return (
    <Typography
      variant="caption"
      fontSize="0.7rem"
      fontWeight={isOld ? 400 : 700}
      textAlign="right"
      sx={{
        color: isOld ? "error.main" : "success.dark",
        textDecoration: isOld ? "line-through" : "none",
        opacity: isOld ? 0.8 : 1,
      }}
    >
      {displayVal}
    </Typography>
  );
};
